import type { InstrumentStatusView } from "@screener/protocol";
import { marketKey } from "@/density/stream-state";
import { tickerLabel, venueOf } from "@/lib/labels";

/**
 * Инструмент без данных, о котором плашка «N без данных» говорит: не `live` и
 * не на рынке, который фронт сообщил недоступным (§132 п. 5) — о тех уже
 * говорит плашка недоступных рынков. Отбор по тому, что сообщил фронт.
 */
function isNotLive(entry: InstrumentStatusView, marketsDown: ReadonlyMap<string, number>): boolean {
  return (
    entry.status !== "live" &&
    !marketsDown.has(marketKey(entry.instrument.exchange, entry.instrument.market))
  );
}

/** Число инструментов без данных, не считая рынков из `marketsDown`. */
export function countNotLive(
  instruments: readonly InstrumentStatusView[],
  marketsDown: ReadonlyMap<string, number>,
): number {
  return instruments.filter((entry) => isNotLive(entry, marketsDown)).length;
}

/**
 * Сколько тикеров одной биржи и рынка называется в подсказке плашки
 * «N без данных»; остальные — «и ещё N». Подсказка — компактная справка, а
 * не полный перечень: разрыв одного соединения даёт 20 инструментов сразу.
 */
export const NOT_LIVE_TICKERS_PER_VENUE = 12;

/**
 * Текст подсказки плашки «N без данных» (§88 п. 7, решение владельца
 * 14.10.2026): по строке на биржу и рынок — «Kraken · SPOT (20): EDU/USD,
 * EVAA/USD, … и ещё 8». Строки — от биржи с наибольшим числом, тикеры — по
 * алфавиту, запись тикера — как на экране (`tickerLabel`, §63).
 *
 * Возвращает строку, а не структуру: селектор потока сравнивает результат
 * `Object.is`, и плашка перерисовывается только когда сменился сам текст.
 * Пустая строка — все инструменты отдают данные.
 */
export function notLiveSummary(
  instruments: readonly InstrumentStatusView[],
  marketsDown: ReadonlyMap<string, number> = new Map(),
): string {
  const venues = new Map<string, string[]>();
  for (const entry of instruments) {
    if (!isNotLive(entry, marketsDown)) {
      continue;
    }
    const venue = venueOf(entry.instrument);
    const ticker = tickerLabel(entry.instrument, entry.exchange_base ?? "");
    const tickers = venues.get(venue);
    if (tickers === undefined) {
      venues.set(venue, [ticker]);
    } else {
      tickers.push(ticker);
    }
  }
  return [...venues]
    .sort(([venueA, a], [venueB, b]) => b.length - a.length || venueA.localeCompare(venueB))
    .map(([venue, tickers]) => {
      const sorted = [...tickers].sort((a, b) => a.localeCompare(b));
      const shown = sorted.slice(0, NOT_LIVE_TICKERS_PER_VENUE).join(", ");
      const rest = sorted.length - NOT_LIVE_TICKERS_PER_VENUE;
      return `${venue} (${sorted.length}): ${shown}${rest > 0 ? ` и ещё ${rest}` : ""}`;
    })
    .join("\n");
}
